import { KinematicsGraphV2 } from '@/pages/ReportV2/components/Graphs';
/** The Run Kinematics report tab */
import { IonCardContent, IonCardTitle, IonCol, IonFabButton, IonGrid, IonIcon, IonRow, IonText } from '@ionic/react';
import { colorPalette } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';

import { CardHeader } from '@/components/CardHeader/cardHeader';
import { KinematicsCardHeader } from '@/components/CardHeader/KinematicsCardHeader';
import { Card } from '@/components/Cards';
import { DataTable } from '@/components/DataTable/DataTable';
import { DataTableColumn } from '@/components/DataTable/DataTableColumn';
import { DataTableHeader } from '@/components/DataTable/DataTableHeader';
import { DataTableSection } from '@/components/DataTable/DataTableSection';
import { FloatingButton } from '@/components/FloatingButton/FloatingButton';
import { Icon } from '@/components/Icon';
import { IconSize } from '@/components/Icon/types';
import { InfoToast } from '@/components/Toasts/InfoToast';
import { LegendToast } from '@/components/Toasts/LegendToast';

import { mergeClassNames } from '@/utils/mergeClassNames';

import toggle from '@/assets/icons/toggle-left.svg';

import { WalkKinematicData } from '@/models/reportV2.model';

// Styles
import styles from '@/pages/ReportV2/Styles/Reports.module.css';

type KinematicsReportProps = {
  tabData: WalkKinematicData;
  setFloatingButtons: (buttons: React.ReactNode) => void;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const WalkKinematicsReportTab = ({ tabData, setFloatingButtons }: KinematicsReportProps) => {
  const [showAllStrides, setShowAllStrides] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [showInfoToast, setShowInfoToast] = useState(false);

  const handleShowToast = () => {
    setShowToast(true);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const romData = tabData.romData;
  const tableData = tabData.tableData;

  const toggleShowAllStrides = () => {
    setShowAllStrides((prev) => {
      const newValue = !prev;
      localStorage.setItem('showAllStridesKinematics', newValue ? 'true' : 'false');
      return newValue;
    });
  };

  const seriesPelvis = useMemo(() => {
    return showAllStrides ? tabData.pelvisKinematicData.allStrides : tabData.pelvisKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesHip = useMemo(() => {
    return showAllStrides ? tabData.hipKinematicData.allStrides : tabData.hipKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesKnee = useMemo(() => {
    return showAllStrides ? tabData.kneeKinematicData.allStrides : tabData.kneeKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesAnkle = useMemo(() => {
    return showAllStrides ? tabData.ankleKinematicData.allStrides : tabData.ankleKinematicData.mean;
  }, [showAllStrides, tabData]);

  useEffect(() => {
    setShowInfoToast(true);
    return () => {
      setShowInfoToast(false);
    };
  }, []);

  useEffect(() => {
    const buttons = (
      <>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Strides'}>
          <IonIcon icon={toggle}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={handleShowToast} title={'Show Graph Legend'}>
          <IonIcon icon={colorPalette}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={() => setShowInfoToast(true)} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </>
    );
    setFloatingButtons(buttons); // Update buttons in parent
    return () => setFloatingButtons(null); // Clean up on unmount
  }, [setFloatingButtons]);

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card style={{ height: 'fit-content', marginLeft: 0, marginRight: 0, marginTop: 0 }}>
            <KinematicsCardHeader />
            <IonCardContent>
              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px', backgroundColor: 'transparent' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Pelvis</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Anterior tilt (-) / Posterior tilt (+)'}
                        title={'Pelvis - Sagittal'}
                        yAxis={seriesPelvis.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Drop (-) / Hike (+)'}
                        title={'Pelvis - Frontal'}
                        yAxis={seriesPelvis.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesPelvis.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Backward Rotation (-) / Forward Rotation (+)'}
                        title={'Pelvis - Transversal'}
                        yAxis={seriesPelvis.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Hip</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Hip - Sagittal'}
                        yAxis={seriesHip.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Abduction (-) / Adduction (+)'}
                        title={'Hip - Frontal'}
                        yAxis={seriesHip.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesHip.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Hip - Transversal'}
                        yAxis={seriesHip.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Knee</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Knee - Sagittal'}
                        yAxis={seriesKnee.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Valgus (-) / Varus (+)'}
                        title={'Knee - Frontal'}
                        yAxis={seriesKnee.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesKnee.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Knee - Transversal'}
                        yAxis={seriesKnee.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Ankle</IonCardTitle>
                  </IonRow>
                  <IonRow>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Sagittal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Plantarflexion (-) / Dorsalflexion (+)'}
                        title={'Ankle - Sagittal'}
                        yAxis={seriesAnkle.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Coronal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Eversion (-) / Inversion (+)'}
                        title={'Ankle - Frontal'}
                        yAxis={seriesAnkle.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <KinematicsGraphV2
                        data={seriesAnkle.Transversal.series}
                        graphAnnotations={tabData.graphAnnotations}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Ankle - Transversal'}
                        yAxis={seriesAnkle.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>
      {/* Tables */}
      <IonRow class='ion-no-padding'>
        <IonCol size='6'>
          <Card style={{ margin: 0 }}>
            <CardHeader title={'Range of Motion'} />
            <IonCardContent>
              <IonGrid className='ion-no-padding'>
                <IonRow
                  className={mergeClassNames(styles.full_height, 'ion-padding-vertical', 'ion-align-items-start')}>
                  <DataTable>
                    <DataTableHeader titles={['', 'Left', 'Right', 'Symmetry']} />
                    <DataTableSection>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn type='key'>Pelvis</DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Pelvis.Left} type='value'>
                          {romData[0][0].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Pelvis.Right} type='value'>
                          {romData[0][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Pelvis.Symmetry} type='value'>
                          {romData[0][2].toFixed(0) + '%'}
                        </DataTableColumn>
                      </IonRow>
                    </DataTableSection>
                    <DataTableSection>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn type='key'>Hip</DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Hip.Left} type='value'>
                          {romData[1][0].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Hip.Right} type='value'>
                          {romData[1][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Hip.Symmetry} type='value'>
                          {romData[1][2].toFixed(0) + '%'}
                        </DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn indent type='key'>
                          Flexion
                        </DataTableColumn>
                        <DataTableColumn type='value'>{romData[2][0].toFixed(1) + '°'}</DataTableColumn>
                        <DataTableColumn type='value'>{romData[2][1].toFixed(1) + '°'}</DataTableColumn>
                        <DataTableColumn type='value'></DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn indent type='key'>
                          Extension
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Hip.ExtensionLeft} type='value'>
                          {romData[3][0].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Hip.ExtensionRight} type='value'>
                          {romData[3][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'></DataTableColumn>
                      </IonRow>
                    </DataTableSection>
                    <DataTableSection>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn type='key'>Knee</DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Knee.Left} type='value'>
                          {romData[4][0].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Knee.Right} type='value'>
                          {romData[4][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Knee.Symmetry} type='value'>
                          {romData[4][2].toFixed(0) + '%'}
                        </DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn indent type='key'>
                          Stance flexion
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Knee.stanceFlexionLeft} type='value'>
                          {romData[5][0].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Knee.stanceFlexionRight} type='value'>
                          {romData[5][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'></DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn indent type='key'>
                          Varus Valgus
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Knee.varusValgusLeft} type='value'>
                          {romData[6][0].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Knee.varusValgusRight} type='value'>
                          {romData[6][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn type='value'></DataTableColumn>
                      </IonRow>
                    </DataTableSection>
                    <DataTableSection>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn type='key'>Ankle</DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Ankle.Left} type='value'>
                          {romData[7][0].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Ankle.Right} type='value'>
                          {romData[7][1].toFixed(1) + '°'}
                        </DataTableColumn>
                        <DataTableColumn label={tabData.tableClassNames.Ankle.Symmetry} type='value'>
                          {romData[7][2].toFixed(0) + '%'}
                        </DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn indent type='key'>
                          Dorsal flexion
                        </DataTableColumn>
                        <DataTableColumn type='value'>{romData[8][0].toFixed(1) + '°'}</DataTableColumn>
                        <DataTableColumn type='value'>{romData[8][1].toFixed(1) + '°'}</DataTableColumn>
                        <DataTableColumn type='value'></DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn indent type='key'>
                          Plantar flexion
                        </DataTableColumn>
                        <DataTableColumn type='value'>{romData[9][0].toFixed(1) + '°'}</DataTableColumn>
                        <DataTableColumn type='value'>{romData[9][1].toFixed(1) + '°'}</DataTableColumn>
                        <DataTableColumn type='value'></DataTableColumn>
                      </IonRow>
                    </DataTableSection>
                  </DataTable>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </Card>
        </IonCol>

        <IonCol size='6'>
          <Card style={{ margin: 0, height: '100%' }}>
            <CardHeader title={'Spatio temporal parameters'} />
            <IonCardContent>
              <IonGrid className='ion-no-padding'>
                <IonRow
                  className={mergeClassNames(styles.full_height, 'ion-padding-vertical', 'ion-align-items-start')}>
                  <DataTable>
                    <DataTableHeader titles={['', 'Left', 'Right']} />
                    <DataTableSection>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn subHeader={'(Percentage of stride time)'} type='key'>
                          Stance phase
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tableData[3][1].toFixed(0) + ' %'}</DataTableColumn>
                        <DataTableColumn type='value'>{tableData[4][1].toFixed(0) + ' %'}</DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn subHeader={'(Seconds)'} type='key'>
                          Stance phase
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tableData[0][1].toFixed(2) + ' s'}</DataTableColumn>
                        <DataTableColumn type='value'>{tableData[1][1].toFixed(2) + ' s'}</DataTableColumn>
                      </IonRow>
                    </DataTableSection>
                    <DataTableSection>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn subHeader={'(Percentage of stride time)'} type='key'>
                          Swing phase
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tableData[3][2].toFixed(0) + ' %'}</DataTableColumn>
                        <DataTableColumn type='value'>{tableData[4][2].toFixed(0) + ' %'}</DataTableColumn>
                      </IonRow>
                      <IonRow style={{ width: '100%' }}>
                        <DataTableColumn subHeader={'(Seconds)'} type='key'>
                          Swing phase
                        </DataTableColumn>
                        <DataTableColumn type='value'>{tableData[0][2].toFixed(2) + ' s'}</DataTableColumn>
                        <DataTableColumn type='value'>{tableData[1][2].toFixed(2) + ' s'}</DataTableColumn>
                      </IonRow>
                    </DataTableSection>
                  </DataTable>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>

      <FloatingButton
        buttonIcon='sliders'
        horizontal='end'
        iconSize={IconSize.large}
        side='top'
        slot='fixed'
        vertical='bottom'>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Strides'}>
          <IonIcon icon={toggle}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={handleShowToast} title={'Show Graph Legend'}>
          <IonIcon icon={colorPalette}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={() => setShowInfoToast(true)} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </FloatingButton>

      <LegendToast
        duration={3000} // 3 seconds
        isOpen={showToast}
        onClose={handleCloseToast}
      />

      <InfoToast
        isOpen={showInfoToast}
        message='All graphs can be enlarged by clicking / tapping on the graph.'
        onClose={() => setShowInfoToast(false)}
      />
    </>
  );
};
